import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { Building2, Radio, Download, Target, ClipboardCheck } from "lucide-react";

const Node = ({ icon: Icon, title, sub, className, z = 40 }) => (
  <div className={`absolute flex items-center gap-3 rounded-2xl border border-white/70 bg-white/90 px-4 py-3 shadow-[0_20px_50px_rgba(16,43,80,0.14)] backdrop-blur-md ${className}`} style={{ transform: `translateZ(${z}px)` }}>
    <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#E8F1FD] text-[#2878E5]"><Icon className="h-5 w-5" strokeWidth={1.8} /></span>
    <span>
      <span className="block font-display text-sm font-bold text-[#102B50]">{title}</span>
      <span className="block text-xs text-[#5A6D88]">{sub}</span>
    </span>
  </div>
);

const Chip = ({ icon: Icon, label, className, z }) => (
  <div className={`absolute inline-flex items-center gap-1.5 rounded-full bg-[#102B50] px-3 py-1.5 text-xs font-semibold text-white shadow-lg ${className}`} style={{ transform: `translateZ(${z}px)` }}>
    <Icon className="h-3.5 w-3.5 text-[#D9A93F]" />{label}
  </div>
);

export const HeroVisual = () => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [10, -10]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-12, 12]), { stiffness: 120, damping: 18 });
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 600], [0, 70]);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onLeave = () => { mx.set(0); my.set(0); };

  return (
    <motion.div data-testid="hero-visual" style={{ y }} className="relative mx-auto aspect-square w-full max-w-[540px] [perspective:1200px]" onMouseMove={onMove} onMouseLeave={onLeave}
      initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}>
      <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }} className="relative h-full w-full">
        <div className="absolute inset-[6%] rounded-full bg-gradient-to-br from-[#E8F1FD] via-white to-[#F4F7FB] shadow-[inset_0_0_80px_rgba(40,120,229,0.12)]" />
        <svg viewBox="0 0 400 400" className="absolute inset-0 h-full w-full" aria-hidden>
          <circle cx="200" cy="200" r="150" fill="none" stroke="#2878E5" strokeOpacity=".18" strokeDasharray="3 6" className="spin-slow origin-center" style={{ transformOrigin: "200px 200px" }} />
          <circle cx="200" cy="200" r="104" fill="none" stroke="#102B50" strokeOpacity=".10" />
          <path d="M70 110 C 140 110, 150 200, 200 200" fill="none" stroke="#2878E5" strokeWidth="1.6" className="flow-dash" />
          <path d="M200 200 C 250 200, 260 300, 330 300" fill="none" stroke="#2878E5" strokeWidth="1.6" className="flow-dash" />
          <path d="M330 110 C 270 110, 250 200, 200 200" fill="none" stroke="#D9A93F" strokeWidth="1.4" className="flow-dash" />
          <path d="M200 200 C 150 200, 140 300, 70 300" fill="none" stroke="#D9A93F" strokeWidth="1.4" className="flow-dash" />
        </svg>
        <div className="absolute left-1/2 top-1/2 flex h-[30%] w-[30%] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-[28%] bg-white shadow-[0_30px_80px_rgba(16,43,80,0.22)]" style={{ transform: "translate(-50%,-50%) translateZ(80px)" }}>
          <img src="/favicon.png" alt="" className="h-[70%] w-[70%] object-contain" />
        </div>
        <Node icon={Building2} title="Advertisers" sub="Brands & apps" className="left-0 top-[18%]" z={60} />
        <Node icon={Radio} title="Publishers" sub="Traffic partners" className="bottom-[16%] right-0" z={60} />
        <Chip icon={Download} label="CPI" className="right-[8%] top-[20%]" z={100} />
        <Chip icon={Target} label="CPA" className="bottom-[22%] left-[6%]" z={90} />
        <Chip icon={ClipboardCheck} label="CPL" className="left-[44%] top-[6%]" z={70} />
      </motion.div>
    </motion.div>
  );
};
